import React, { useRef, useCallback, useEffect, useState } from 'react';
import { tr } from '../../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import AppButton from '../AppButton/AppButton';

export interface SignaturePadProps {
  // Core props
  value?: string; // base64 signature data URL
  onChange: (signatureData: string) => void; // callback with base64 output
  onError?: (error: string) => void; // error callback

  // State props
  disabled?: boolean;
  error?: string | null;
  showErrorMessage?: boolean; // show error message below canvas

  // Styling props
  width?: string; // Tailwind class (default: 'w-full')
  height?: string; // Tailwind class for height (default: 'h-40')
  penColor?: string; // signature pen color (default: brand purple)
  backgroundColor?: string; // canvas background (default: white)
  borderClassName?: string; // custom border class
  containerClassName?: string; // custom container class
  canvasClassName?: string; // custom canvas class
  labelClassName?: string; // custom label class

  // Signature pad config props
  label?: string; // optional label above canvas
  showClearButton?: boolean; // show clear button (default: true)
  showStatus?: boolean; // show 'Signature captured' status (default: true)

  // Pen stroke config (optional)
  penWidthMin?: number; // default: 0.5
  penWidthMax?: number; // default: 2.5
  velocityFilterWeight?: number; // default: 0.7
  minDistance?: number; // default: 5
  throttle?: number; // default: 16

  // Accessibility
  ariaLabel?: string;
  ariaDescribedBy?: string;
}

const SignaturePad = React.forwardRef<SignatureCanvas, SignaturePadProps>(
  (
    {
      value = '',
      onChange,
      onError,
      disabled = false,
      error = null,
      showErrorMessage = true,
      width = 'w-full',
      height = 'h-40',
      penColor = '#6c4e9a', // Classveew brand purple
      backgroundColor = 'rgba(255, 255, 255, 1)',
      borderClassName = '',
      containerClassName = '',
      canvasClassName = '',
      labelClassName = '',
      label,
      showClearButton = true,
      showStatus = true,
      penWidthMin = 0.5,
      penWidthMax = 2.5,
      velocityFilterWeight = 0.7,
      minDistance = 5,
      throttle = 16,
      ariaLabel = 'Draw your signature',
      ariaDescribedBy,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const canvasRef = useRef<SignatureCanvas>(null);
    const [isSigned, setIsSigned] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);

    // Update ref to use our canvas ref
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(canvasRef.current);
        } else {
          ref.current = canvasRef.current;
        }
      }
    }, [ref]);

    // Load existing signature from value prop
    useEffect(() => {
      if (value && canvasRef.current && !isDrawing) {
        try {
          canvasRef.current.fromDataURL(value);
          setIsSigned(true);
        } catch (err) {
          console.warn('Failed to load signature from data URL:', err);
        }
      }
    }, [value, isDrawing]);

    // Handle disabled state
    useEffect(() => {
      if (canvasRef.current) {
        if (disabled) {
          canvasRef.current.off(); // Disable drawing
        } else {
          canvasRef.current.on(); // Enable drawing
        }
      }
    }, [disabled]);

    // Handle signature start
    const handleSignatureBegin = useCallback(() => {
      setIsDrawing(true);
    }, []);

    // Handle signature end - export to base64
    const handleSignatureEnd = useCallback(() => {
      setIsDrawing(false);

      if (canvasRef.current) {
        // Check if signature is empty
        if (canvasRef.current.isEmpty()) {
          setIsSigned(false);
          onChange('');
          return;
        }

        // Export to base64
        try {
          const signatureData = canvasRef.current.toDataURL('image/png');
          setIsSigned(true);
          onChange(signatureData);

          // Clear error on successful signature
          if (onError) {
            onError('');
          }
        } catch (err) {
          const errorMsg = tr('g.x_failed_to_capture_signature', 'Failed to capture signature');
          console.error(errorMsg, err);
          if (onError) {
            onError(errorMsg);
          }
        }
      }
    }, [onChange, onError]);

    // Handle clear button
    const handleClear = useCallback(() => {
      if (canvasRef.current) {
        canvasRef.current.clear();
        setIsSigned(false);
        onChange('');
      }
    }, [onChange]);

    // Determine border color based on state
    const borderClass = borderClassName
      ? borderClassName
      : error
      ? 'border-red-500 border-2'
      : isSigned
      ? 'border-green-500 border-2'
      : 'border border-gray-300';

    // Determine focus ring class
    const focusRingClass = disabled ? '' : 'focus-within:ring-1 focus-within:ring-[#6c4e9a]';

    // Determine cursor based on disabled state
    const cursorClass = disabled ? 'cursor-not-allowed' : 'cursor-crosshair';

    return (
      <div className={`flex flex-col gap-2 ${containerClassName}`}>
        {/* Label */}
        {label && (
          <label className={`text-sm font-semibold text-gray-700 font-poppins ${labelClassName}`}>
            {label}
            {/* Optional: Add required indicator */}
          </label>
        )}

        {/* Canvas Container */}
        <div
          className={`${width} rounded-md overflow-hidden ${borderClass} ${focusRingClass} transition-all duration-200`}
        >
          <SignatureCanvas
            ref={canvasRef}
            penColor={penColor}
            canvasProps={{
              className: `${height} ${width} ${cursorClass} bg-white ${canvasClassName}`,
              'aria-label': ariaLabel,
              'aria-describedby': ariaDescribedBy,
              'aria-invalid': !!error,
            }}
            backgroundColor={backgroundColor}
            onEnd={handleSignatureEnd}
            onBegin={handleSignatureBegin}
            velocityFilterWeight={velocityFilterWeight}
            minWidth={penWidthMin}
            maxWidth={penWidthMax}
            minDistance={minDistance}
            throttle={throttle}
          />
        </div>

        {/* Status & Controls Row */}
        <div className="flex items-center justify-between gap-3">
          {/* Status indicator - grows to take available space */}
          {showStatus && (
            <div className="flex-1 text-xs font-poppins">
              {isSigned ? (
                <span className="flex items-center gap-1 text-green-600">
                  <span className="inline-block w-2 h-2 bg-green-600 rounded-full"></span>{t('g.signature_captured', 'Signature captured')}</span>
              ) : (
                <span className="text-gray-500">{t('common.not_signed', 'Not signed')}</span>
              )}
            </div>
          )}

          {/* Clear Button - fixed width (w-1/4) and aligned on same row */}
          {showClearButton && isSigned && (
            <div className="w-1/4 flex-shrink-0">
              <AppButton
                label={t('common.button_clear')}
                text
                onClick={handleClear}
                disabled={disabled}
                className="w-full text-xs px-3 py-1"
                aria-label={t('g.clear_signature', 'Clear signature')}
              />
            </div>
          )}
        </div>

        {/* Error Message */}
        {showErrorMessage && error && (
          <p className="text-xs text-red-600 mt-1 font-poppins">{error}</p>
        )}

        {/* Helper Text */}
        {!isSigned && (
          <p className="text-xs text-gray-500 font-poppins italic">
            {disabled
              ? tr('g.sign_after_you_read_the_agreement', 'Sign after you read the agreement')
              : tr('g.draw_your_signature_above_with_mouse_or_finger', 'Draw your signature above with mouse or finger')}
          </p>
        )}
      </div>
    );
  },
);

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
